/**
 * Severity ladder (art-direction §8.2: hit-stop table §8.2.1 and the ladder §8.2.2). Owner: Engine (F2) values; the VFX
 * director (P10) and the combat director (P4) read them. Every time value is ms of real time at ×1. Pure data.
 */
import type { ElementInk } from '../../shared';

export type Severity = 'light' | 'heavy' | 'crit' | 'ko';
/** combat §5.5 swing classes (hit / heavy / crit hit-stop, ms). */
export const SWING_HITSTOP: Record<string, [number, number, number]> = {
  stab: [40, 60, 100], paw_swipe: [50, 70, 110], slap: [50, 70, 110], staff_twirl: [50, 70, 110],
  club_swing: [60, 80, 120], blade_slash: [60, 80, 120], instrument: [60, 80, 120], book_slam: [70, 90, 130],
  heavy_overhead: [80, 100, 140], couched_charge: [90, 110, 150], flail_spin: [40, 60, 100],
  throw_arc: [60, 80, 120], throw_roll: [60, 80, 120], shoot: [40, 60, 100], pet_lunge: [30, 40, 80], skill: [60, 80, 120],
};
/** Non-swing rows of §8.2.1. */
export const OTHER_HITSTOP = { ko: 233, block: 65, parry: 85, ward: 65, deflect: 100, debuff: 35, miss: 0, heal: 0, buff: 0, dot: 0 } as const;
/** Heavy = class hit + 20, capped at 110 (the table already holds it; kept for the rule). */
export const HEAVY_EXTRA = { addMs: 20, capMs: 110 } as const;

export function hitStopMs(cls: string, severity: Severity): number {
  if (severity === 'ko') return OTHER_HITSTOP.ko;
  const row = SWING_HITSTOP[cls] ?? SWING_HITSTOP.skill;
  return severity === 'light' ? row[0] : severity === 'heavy' ? row[1] : row[2];
}

export interface LadderRow {
  flash: { color: 'leadWhite' | 'vermilion' | 'lapisPale' | 'gold' | 'element'; amount: number; ms: number; rimOnly?: boolean } | null;
  shakePx: number;
  punchPct: number;
  particles: Partial<Record<'splat' | 'blob' | 'tuft' | 'dust' | 'sparkle' | 'rubric' | 'clang' | 'speedHatch' | 'element', number>>;
  decals: number;
  impactFrame: boolean;
  numberPx: number;
  element?: ElementInk;
}
/** §8.2.2 (everything except hit-stop). Shake px are at 390 width, scaled by panel width / 358. */
export const LADDER: Record<'miss' | 'block' | 'parry' | 'ward' | 'deflect' | Severity | 'heal' | 'debuff' | 'dot', LadderRow> = {
  miss: { flash: null, shakePx: 0, punchPct: 0, particles: { dust: 3, speedHatch: 2 }, decals: 0, impactFrame: false, numberPx: 0 },
  block: { flash: { color: 'lapisPale', amount: 1, ms: 33, rimOnly: true }, shakePx: 3, punchPct: 0, particles: { sparkle: 5, clang: 1 }, decals: 0, impactFrame: false, numberPx: 0 },
  parry: { flash: { color: 'leadWhite', amount: 1, ms: 33, rimOnly: true }, shakePx: 4, punchPct: 2, particles: { sparkle: 7, clang: 1 }, decals: 0, impactFrame: false, numberPx: 0 },
  ward: { flash: { color: 'gold', amount: 1, ms: 50, rimOnly: true }, shakePx: 2, punchPct: 0, particles: { sparkle: 6 }, decals: 0, impactFrame: false, numberPx: 0 },
  deflect: { flash: { color: 'gold', amount: 1, ms: 50, rimOnly: true }, shakePx: 4, punchPct: 3, particles: { sparkle: 10 }, decals: 0, impactFrame: false, numberPx: 0 },
  light: { flash: { color: 'leadWhite', amount: 0.6, ms: 33 }, shakePx: 2, punchPct: 0, particles: { splat: 6, tuft: 2 }, decals: 1, impactFrame: false, numberPx: 20, element: 'ink' },
  heavy: { flash: { color: 'leadWhite', amount: 0.7, ms: 50 }, shakePx: 5, punchPct: 3, particles: { splat: 14, tuft: 4, dust: 3 }, decals: 2, impactFrame: false, numberPx: 26, element: 'ink' },
  crit: { flash: { color: 'vermilion', amount: 0.7, ms: 50 }, shakePx: 9, punchPct: 6, particles: { splat: 26, blob: 3, tuft: 6, sparkle: 8, rubric: 1 }, decals: 3, impactFrame: true, numberPx: 34, element: 'vermilion' },
  ko: { flash: { color: 'vermilion', amount: 0.7, ms: 50 }, shakePx: 14, punchPct: 10, particles: { splat: 32, tuft: 10, dust: 7 }, decals: 4, impactFrame: true, numberPx: 0, element: 'ink' },
  heal: { flash: { color: 'gold', amount: 1, ms: 300, rimOnly: true }, shakePx: 0, punchPct: 0, particles: { sparkle: 10 }, decals: 0, impactFrame: false, numberPx: 20 },
  debuff: { flash: { color: 'element', amount: 1, ms: 33 }, shakePx: 2, punchPct: 0, particles: { element: 6 }, decals: 0, impactFrame: false, numberPx: 0 },
  dot: { flash: { color: 'element', amount: 1, ms: 17 }, shakePx: 0, punchPct: 0, particles: { element: 3 }, decals: 0, impactFrame: false, numberPx: 16 },
};

/** Severity of a resolve (one rule for art, combat and creatures, §8.2). */
export function severityOf(r: { ko: boolean; crit: boolean; damage: number; victimMaxHp: number }): Severity {
  if (r.ko) return 'ko';
  if (r.crit) return 'crit';
  return r.damage >= 0.12 * r.victimMaxHp ? 'heavy' : 'light';
}

/** §2.9 element → primary / secondary token. */
export const ELEMENT_COLORS: Record<ElementInk, [string, string]> = {
  ink: ['ink', 'inkFaded'], vermilion: ['vermilion', 'gold'], gold: ['gold', 'glint'], minium: ['minium', 'vermilion'],
  verdigris: ['verdigris', 'verdigrisLight'], folium: ['inkDeep', 'folium'], lapis: ['azurite', 'leadWhite'],
};
